/**
 * Kyoho grape landing: per-letter bubble + sheet bulge; touch orbital drives both from the bouncing grape.
 */
(function () {
  'use strict';

  var SCROLL_LOCK_CLASS = 'kyoho-grape-scroll-lock';
  var KLAVIYO_HIDE_CLASS = 'kyoho-grape-hide-klaviyo';
  var KLAVIYO_HIDE_COUNT_KEY = 'kyohoGrapeKlaviyoHideCount';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCoarsePointer() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  /** Safari (macOS + iOS): perspective + animated 3D on large layers can corrupt the GPU compositor (solid red viewport). */
  function isSafariWithoutChrome() {
    var ua = navigator.userAgent || '';
    if (!/Safari/i.test(ua)) {
      return false;
    }
    if (/Chrome|CriOS|Chromium|Edg|Android|OPR|Opera/i.test(ua)) {
      return false;
    }
    return true;
  }

  /** Pointer left the page DOM (e.g. docked Web Inspector) — not the same as moving between elements. */
  function docPointerExitToChrome(relatedTarget) {
    return (
      relatedTarget == null ||
      !document.documentElement.contains(relatedTarget)
    );
  }

  function finiteOr(x, fallback) {
    return typeof x === 'number' && isFinite(x) ? x : fallback;
  }

  function enableKlaviyoSuppression() {
    var docEl = document.documentElement;
    var count = parseInt(docEl.dataset[KLAVIYO_HIDE_COUNT_KEY], 10) || 0;
    count += 1;
    docEl.dataset[KLAVIYO_HIDE_COUNT_KEY] = String(count);
    docEl.classList.add(KLAVIYO_HIDE_CLASS);
  }

  function disableKlaviyoSuppression() {
    var docEl = document.documentElement;
    var count = parseInt(docEl.dataset[KLAVIYO_HIDE_COUNT_KEY], 10) || 0;
    count = Math.max(0, count - 1);
    if (count === 0) {
      delete docEl.dataset[KLAVIYO_HIDE_COUNT_KEY];
      docEl.classList.remove(KLAVIYO_HIDE_CLASS);
      return;
    }
    docEl.dataset[KLAVIYO_HIDE_COUNT_KEY] = String(count);
  }

  function parseData(root) {
    return {
      bubbleRadius: parseFloat(root.dataset.bubbleRadius) || 160,
      bubbleStrength: parseFloat(root.dataset.bubbleStrength) || 0.28,
      driftStrength: parseFloat(root.dataset.driftStrength) || 10,
      smooth: Math.min(
        0.45,
        Math.max(0.08, parseFloat(root.dataset.bubbleSmooth) || 0.22)
      ),
      sheetStrength: Math.max(
        0,
        Math.min(0.85, parseFloat(root.dataset.sheetStrength) || 0)
      ),
      sheetPerspective: parseInt(root.dataset.sheetPerspective, 10) || 1200,
      fisheyeBoost: Math.max(
        0.8,
        Math.min(2.4, parseFloat(root.dataset.fisheyeBoost) || 1.4)
      ),
      viscosity: Math.max(
        0.1,
        Math.min(0.95, parseFloat(root.dataset.viscosity) || 0.62)
      ),
      fisheyeRenderMode: root.dataset.fisheyeRenderMode || 'letters',
      mobileOrbital: root.dataset.mobileOrbital === 'true',
      orbitalSpeed: Math.max(
        0.004,
        Math.min(0.055, parseFloat(root.dataset.orbitalSpeed) || 0.02)
      ),
      desktopSpacingMaxChars: Math.max(
        0,
        Math.min(220, parseInt(root.dataset.desktopSpacingMaxChars, 10) || 36)
      ),
      desktopSpacingMaxPx: Math.max(
        0,
        Math.min(28, parseFloat(root.dataset.desktopSpacingMaxPx) || 5)
      ),
      mobileSpacingMaxChars: Math.max(
        0,
        Math.min(220, parseInt(root.dataset.mobileSpacingMaxChars, 10) || 22)
      ),
      mobileSpacingMaxPx: Math.max(
        0,
        Math.min(24, parseFloat(root.dataset.mobileSpacingMaxPx) || 8)
      ),
      sectionLink: root.dataset.sectionLink || '',
      debugLinkSource: root.dataset.debugLinkSource || 'none',
      immersive: root.classList.contains('kyoho-grape--immersive'),
      sectionId: root.getAttribute('data-section-id') || '',
    };
  }

  function initSection(root) {
    if (!root || root.dataset.kyohoInitialized === 'true') return;
    root.dataset.kyohoInitialized = 'true';

    var params = parseData(root);
    var cursor = root.querySelector('[data-kyoho-cursor]');
    var sheet = root.querySelector('[data-kyoho-sheet]');
    var letters = root.querySelectorAll('.kyoho-bubble-letter');
    var svgMode = params.fisheyeRenderMode === 'svg_full' || params.fisheyeRenderMode === 'svg_active_zone';
    var svgFullMode = params.fisheyeRenderMode === 'svg_full';
    var n = letters.length;
    var klaviyoSuppressed = false;
    var sectionUnloaded = false;
    var sectionClickHandler = null;
    var debugPrefix = '[kyoho-link-debug ' + (params.sectionId || 'unknown') + ']';

    function cleanupStaticEffects() {
      if (sectionUnloaded) {
        return;
      }
      sectionUnloaded = true;
      if (params.immersive) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      }
      if (klaviyoSuppressed) {
        disableKlaviyoSuppression();
        klaviyoSuppressed = false;
      }
    }

    enableKlaviyoSuppression();
    klaviyoSuppressed = true;
    var onSectionUnloadCleanup = function () {};

    root.dataset.kyohoLinkResolved = params.sectionLink || '';
    root.dataset.kyohoLinkSource = params.debugLinkSource || 'none';
    console.info(
      debugPrefix,
      'init',
      'source=',
      params.debugLinkSource,
      'resolved=',
      params.sectionLink || '(blank)'
    );

    if (params.sectionLink) {
      root.classList.add('kyoho-grape--link-active');
      sectionClickHandler = function (e) {
        console.info(
          debugPrefix,
          'click',
          'defaultPrevented=',
          e.defaultPrevented,
          'button=',
          e.button,
          'target=',
          e.target && e.target.tagName
        );
        if (e.defaultPrevented) return;
        if (e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var target = e.target;
        if (target && target.closest && target.closest('a, button, input, textarea, select, label')) {
          console.info(debugPrefix, 'blocked-by-interactive-target');
          return;
        }
        console.info(debugPrefix, 'navigating-to', params.sectionLink);
        window.location.assign(params.sectionLink);
      };
      root.addEventListener('click', sectionClickHandler, true);
      console.info(debugPrefix, 'click-listener-attached');
    } else {
      root.classList.remove('kyoho-grape--link-active');
      console.warn(debugPrefix, 'no-link-resolved');
    }

    document.addEventListener('shopify:section:unload', function (ev) {
      try {
        var sid = ev.detail && ev.detail.sectionId;
        if (sid != null && String(sid) === params.sectionId) {
          cleanupStaticEffects();
          stopLoop();
          if (sectionClickHandler) {
            root.removeEventListener('click', sectionClickHandler, true);
            console.info(debugPrefix, 'click-listener-removed-on-unload');
          }
          onSectionUnloadCleanup();
        }
      } catch (err) {
        /* noop */
      }
    });

    var state = [];
    var i;
    for (i = 0; i < n; i++) {
      state.push({ nx: 0, ny: 0, s: 1, ls: 0, gap: 0, active: false });
    }

    var sheetState = { rx: 0, ry: 0, sc: 1 };
    var svgState = [];

    if (prefersReducedMotion()) {
      root.classList.add('kyoho-grape--reduced');
      return;
    }

    var coarse = isCoarsePointer();
    var narrowViewport = window.matchMedia('(max-width: 749px)').matches;
    /*
      Real phones: coarse pointer. Safari/Chrome device toolbars often report fine pointer even
      when the viewport is phone-sized — treat narrow + mobile_orbital like touch orbital.
    */
    var orbital =
      params.mobileOrbital && (coarse || narrowViewport);
    /* Fancy per-letter spacing only on large viewports with fine pointer (real desktop). */
    var desktopSpacingEnabled = !coarse && !narrowViewport;
    var disableSheet3d = isSafariWithoutChrome();

    if (disableSheet3d) {
      root.classList.add('kyoho-grape--no-sheet-3d');
      if (sheet) {
        sheet.style.transform = '';
        sheet.style.willChange = 'auto';
      }
    }

    if (isSafariWithoutChrome()) {
      root.classList.add('kyoho-grape--safari-gpu-tame');
    }

    if (coarse && !orbital) {
      root.classList.add('kyoho-grape--touch');
      return;
    }

    if (orbital) {
      root.classList.add('kyoho-grape--orbital');
    }

    createSvgState();

    if (params.immersive) {
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
    }

    var mx = window.innerWidth * 0.5;
    var my = window.innerHeight * 0.5;
    var pointerInside = false;
    /** False while the mouse has left the browser viewport (freeze interaction, no reset). */
    var pointerInViewport = true;
    var inView = true;
    var rafId = 0;
    var smooth = params.smooth;
    /** Touch: bouncing “cursor” physics (px, px/s). */
    var bounce = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      lastT: 0,
      ready: false,
    };

    /** Orbital: pointer drag vs tap (CTA link must still receive taps). */
    var orbitalPointerActive = false;
    var orbitalDragActive = false;
    var orbitalPointerId = null;
    var orbitalStartX = 0;
    var orbitalStartY = 0;
    var orbitalLastX = 0;
    var orbitalLastY = 0;
    var orbitalLastT = 0;
    var orbitalDragVx = 0;
    var orbitalDragVy = 0;
    var savedOrbitalVx = 0;
    var savedOrbitalVy = 0;
    var ORBITAL_DRAG_THRESHOLD = 12;
    /** Coarse + orbital: letter/sheet FX are expensive (many getBoundingClientRect); cap ~30fps. */
    var lastOrbitalFxMs = 0;
    var ORBITAL_FX_INTERVAL_MS =
      orbital && isSafariWithoutChrome() ? 56 : 33;
    var orbitalDragRafId = 0;
    var pendingDragClientX = 0;
    var pendingDragClientY = 0;

    function syncOrbitalCursor() {
      mx = bounce.x;
      my = bounce.y;
      if (cursor) {
        cursor.classList.add('is-visible');
        cursor.style.setProperty('--cx', mx + 'px');
        cursor.style.setProperty('--cy', my + 'px');
      }
    }

    function flushOrbitalDragPaint() {
      orbitalDragRafId = 0;
      if (!orbitalDragActive) {
        return;
      }
      var ww = window.innerWidth;
      var wh = window.innerHeight;
      var m = Math.max(40, Math.min(ww, wh) * 0.08);
      bounce.x = Math.max(m, Math.min(ww - m, pendingDragClientX));
      bounce.y = Math.max(m, Math.min(wh - m, pendingDragClientY));
      syncOrbitalCursor();
    }

    function resetLetters() {
      for (i = 0; i < n; i++) {
        letters[i].style.transform = '';
        letters[i].style.letterSpacing = '';
        letters[i].style.marginInlineEnd = '';
        state[i].nx = 0;
        state[i].ny = 0;
        state[i].s = 1;
        state[i].ls = 0;
        state[i].gap = 0;
        state[i].active = false;
      }
    }

    function createSvgState() {
      if (!svgMode) {
        return;
      }
      var segments = root.querySelectorAll('.kyoho-grape__marquee-segment');
      var canvas = document.createElement('canvas');
      var measurer = canvas.getContext('2d');
      if (!measurer) {
        return;
      }
      var segIdx;
      for (segIdx = 0; segIdx < segments.length; segIdx++) {
        var segment = segments[segIdx];
        var textRaw = (segment.textContent || '').replace(/\s+/g, ' ').trim();
        if (!textRaw) {
          continue;
        }
        var style = window.getComputedStyle(segment);
        var fontSize = parseFloat(style.fontSize) || 56;
        var fontFamily = style.fontFamily || 'sans-serif';
        var fontWeight = style.fontWeight || '700';
        var letterSpacingPx = parseFloat(style.letterSpacing);
        if (!isFinite(letterSpacingPx)) {
          letterSpacingPx = 0;
        }
        measurer.font = fontWeight + ' ' + fontSize + 'px ' + fontFamily;
        var glyphs = textRaw.split('');
        var glyphX = new Array(glyphs.length);
        var xCursor = 0;
        var g;
        for (g = 0; g < glyphs.length; g++) {
          glyphX[g] = xCursor;
          var gw = measurer.measureText(glyphs[g]).width;
          xCursor += gw + letterSpacingPx;
        }
        var width = xCursor + fontSize * 0.45;
        var height = fontSize * 1.32;
        var baseline = fontSize * 1.02;
        var ns = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('class', 'kyoho-svg-segment');
        svg.setAttribute('viewBox', '0 0 ' + width.toFixed(2) + ' ' + height.toFixed(2));
        svg.setAttribute('width', width.toFixed(2));
        svg.setAttribute('height', height.toFixed(2));
        svg.setAttribute('preserveAspectRatio', 'none');
        svg.setAttribute('aria-hidden', 'true');
        var textEl = document.createElementNS(ns, 'text');
        textEl.setAttribute('fill', style.color || '#fff');
        textEl.setAttribute('font-size', fontSize.toFixed(2));
        textEl.setAttribute('font-family', fontFamily);
        textEl.setAttribute('font-weight', String(fontWeight));
        textEl.setAttribute('xml:space', 'preserve');
        var glyphNodes = new Array(glyphs.length);
        for (g = 0; g < glyphs.length; g++) {
          var tspan = document.createElementNS(ns, 'tspan');
          tspan.textContent = glyphs[g] === ' ' ? '\u00A0' : glyphs[g];
          tspan.setAttribute('x', glyphX[g].toFixed(2));
          tspan.setAttribute('y', baseline.toFixed(2));
          textEl.appendChild(tspan);
          glyphNodes[g] = tspan;
        }
        svg.appendChild(textEl);
        segment.innerHTML = '';
        segment.classList.add('is-svg-mode');
        segment.appendChild(svg);
        root.classList.add('kyoho-grape--svg-mode');
        svgState.push({
          segment: segment,
          text: textEl,
          width: width,
          baseline: baseline,
          amp: 0,
          ls: Math.max(0, letterSpacingPx),
          rowWeight: 0,
          dir: 0,
          u: 0.5,
          glyphX: glyphX,
          glyphNodes: glyphNodes,
          glyphCount: glyphs.length,
          glyphXNow: glyphX.slice(),
          glyphYNow: new Array(glyphs.length).fill(baseline),
        });
      }
    }

    function resetSvgState() {
      if (!svgMode || svgState.length === 0) {
        return;
      }
      var s;
      for (s = 0; s < svgState.length; s++) {
        var st = svgState[s];
        st.amp = 0;
        st.rowWeight = 0;
        st.dir = 0;
        st.u = 0.5;
        var g;
        for (g = 0; g < st.glyphCount; g++) {
          st.glyphXNow[g] = st.glyphX[g];
          st.glyphYNow[g] = st.baseline;
          st.glyphNodes[g].setAttribute('x', st.glyphX[g].toFixed(2));
          st.glyphNodes[g].setAttribute('y', st.baseline.toFixed(2));
        }
      }
    }

    function resetSheet() {
      sheetState.rx = 0;
      sheetState.ry = 0;
      sheetState.sc = 1;
      if (sheet) {
        sheet.style.transform = '';
      }
    }

    function orbitalSpeedScale() {
      var s = params.orbitalSpeed / 0.02;
      return Math.max(0.45, Math.min(2.4, s));
    }

    function initBouncePhysics(w, h) {
      var scale = orbitalSpeedScale();
      var margin = Math.max(40, Math.min(w, h) * 0.08);
      var minX = margin;
      var maxX = w - margin;
      var minY = margin;
      var maxY = h - margin;
      /* Random direction, constant speed — straight lines until a viewport edge. */
      var angle = Math.random() * Math.PI * 2;
      var speed = (175 + Math.random() * 55) * scale;
      bounce.x = minX + (maxX - minX) * (0.2 + Math.random() * 0.6);
      bounce.y = minY + (maxY - minY) * (0.15 + Math.random() * 0.5);
      bounce.vx = Math.cos(angle) * speed;
      bounce.vy = Math.sin(angle) * speed;
      bounce.lastT = performance.now();
      bounce.ready = true;
    }

    function clampBounceToViewport(w, h, margin) {
      bounce.x = Math.max(margin, Math.min(w - margin, bounce.x));
      bounce.y = Math.max(margin, Math.min(h - margin, bounce.y));
    }

    /**
     * Constant-velocity motion; specular reflection off viewport bounds (straight paths between bounces).
     */
    function updateOrbital(now) {
      var w = window.innerWidth;
      var h = window.innerHeight;
      var margin = Math.max(40, Math.min(w, h) * 0.08);
      var minX = margin;
      var maxX = w - margin;
      var minY = margin;
      var maxY = h - margin;

      if (!bounce.ready) {
        initBouncePhysics(w, h);
      }

      if (orbitalPointerActive && !orbitalDragActive) {
        bounce.lastT = now;
        syncOrbitalCursor();
        return;
      }

      if (orbitalDragActive) {
        bounce.lastT = now;
        syncOrbitalCursor();
        return;
      }

      var dt = (now - bounce.lastT) / 1000;
      bounce.lastT = now;
      if (dt <= 0 || dt > 0.064) {
        dt = Math.min(0.032, 1 / 60);
      }

      bounce.x += bounce.vx * dt;
      bounce.y += bounce.vy * dt;

      var g;
      for (g = 0; g < 6; g++) {
        var bounced = false;
        if (bounce.x < minX) {
          bounce.x = minX;
          bounce.vx = -bounce.vx;
          bounced = true;
        } else if (bounce.x > maxX) {
          bounce.x = maxX;
          bounce.vx = -bounce.vx;
          bounced = true;
        }
        if (bounce.y < minY) {
          bounce.y = minY;
          bounce.vy = -bounce.vy;
          bounced = true;
        } else if (bounce.y > maxY) {
          bounce.y = maxY;
          bounce.vy = -bounce.vy;
          bounced = true;
        }
        if (!bounced) {
          break;
        }
      }

      clampBounceToViewport(w, h, margin);

      syncOrbitalCursor();
    }

    /** Touch: same class of physics as the old simple path, tuned for stronger, wider cursor read. */
    function updateLettersMobileSimple() {
      var MOBILE_RADIUS_MULT = 1.22;
      var MOBILE_STRENGTH_MULT = 1.55;
      var MOBILE_DRIFT_MULT = 1.38;
      var MOBILE_SCALE_EXP = 0.92;
      var r = params.bubbleRadius * MOBILE_RADIUS_MULT;
      var strength = params.bubbleStrength * MOBILE_STRENGTH_MULT;
      var drift = params.driftStrength * MOBILE_DRIFT_MULT;
      var effSmooth = orbital
        ? Math.min(0.45, smooth * 1.75)
        : smooth;
      var rects = new Array(n);
      for (i = 0; i < n; i++) {
        var letter0 = letters[i];
        var anchor0 = letter0.parentElement;
        rects[i] =
          anchor0 && anchor0.getBoundingClientRect
            ? anchor0.getBoundingClientRect()
            : letter0.getBoundingClientRect();
      }
      for (i = 0; i < n; i++) {
        var rect = rects[i];
        var letter = letters[i];
        var cx = rect.left + rect.width * 0.5;
        var cy = rect.top + rect.height * 0.5;
        var dx = mx - cx;
        var dy = my - cy;
        var dist = Math.hypot(dx, dy);
        if (dist < 0.5) {
          dist = 0.5;
        }
        var t = Math.max(0, 1 - dist / r);
        var curve = Math.pow(t, MOBILE_SCALE_EXP);
        var targetScale = 1 + strength * 1.12 * curve;
        var pull = drift * curve;
        var targetNx = (dx / dist) * pull;
        var targetNy = (dy / dist) * pull;

        state[i].nx = lerp(state[i].nx, targetNx, effSmooth);
        state[i].ny = lerp(state[i].ny, targetNy, effSmooth);
        state[i].s = lerp(state[i].s, targetScale, effSmooth);
        state[i].ls = 0;
        state[i].gap = 0;
        state[i].active = false;

        letter.style.transform =
          'translate3d(' +
          state[i].nx.toFixed(2) +
          'px,' +
          state[i].ny.toFixed(2) +
          'px,0) scale(' +
          state[i].s.toFixed(4) +
          ')';
        letter.style.letterSpacing = '';
        letter.style.marginInlineEnd = '';
      }
    }

    function updateLettersDesktop() {
      var r = params.bubbleRadius;
      var boost = params.fisheyeBoost;
      var visc = params.viscosity;
      var strength = params.bubbleStrength * boost;
      var drift = params.driftStrength * Math.min(1.8, 0.85 + boost * 0.6);
      var spacingMaxPx = params.desktopSpacingMaxPx;
      var smoothBase = orbital
        ? Math.min(0.45, smooth * 1.75)
        : smooth;
      var effSmooth = Math.max(0.06, smoothBase * (1 - visc * 0.5));
      var releaseSmooth = Math.max(
        0.05,
        (orbital ? 0.2 : 0.14) * (1 - visc * 0.55)
      );
      var rects = new Array(n);
      var dxCache = new Array(n);
      var dyCache = new Array(n);
      var distCache = new Array(n);
      var curveCache = new Array(n);
      for (i = 0; i < n; i++) {
        var letter0 = letters[i];
        var anchor0 = letter0.parentElement;
        rects[i] =
          anchor0 && anchor0.getBoundingClientRect
            ? anchor0.getBoundingClientRect()
            : letter0.getBoundingClientRect();
        var rect0 = rects[i];
        var cx0 = rect0.left + rect0.width * 0.5;
        var cy0 = rect0.top + rect0.height * 0.5;
        var dx0 = mx - cx0;
        var dy0 = my - cy0;
        var dist0 = Math.max(0.5, Math.hypot(dx0, dy0));
        var t0 = Math.max(0, 1 - dist0 / r);
        var curveExp = Math.max(0.85, 1.6 - boost * 0.35);
        dxCache[i] = dx0;
        dyCache[i] = dy0;
        distCache[i] = dist0;
        curveCache[i] = Math.pow(t0, curveExp);
      }

      var activeMask = null;
      var activeCap = Math.min(n, params.desktopSpacingMaxChars);
      if (activeCap < n) {
        activeMask = new Uint8Array(n);
        var top = [];
        for (i = 0; i < n; i++) {
          var c = curveCache[i];
          if (c <= 0) {
            continue;
          }
          if (top.length === 0) {
            top.push({ idx: i, curve: c });
            continue;
          }
          var inserted = false;
          var j;
          for (j = 0; j < top.length; j++) {
            if (c > top[j].curve) {
              top.splice(j, 0, { idx: i, curve: c });
              inserted = true;
              break;
            }
          }
          if (!inserted && top.length < activeCap) {
            top.push({ idx: i, curve: c });
          }
          if (top.length > activeCap) {
            top.pop();
          }
        }
        for (i = 0; i < top.length; i++) {
          activeMask[top[i].idx] = 1;
        }
      }

      for (i = 0; i < n; i++) {
        var rect = rects[i];
        var letter = letters[i];
        if (activeMask && !activeMask[i]) {
          if (
            state[i].active ||
            Math.abs(state[i].nx) > 0.02 ||
            Math.abs(state[i].ny) > 0.02 ||
            Math.abs(state[i].s - 1) > 0.002 ||
            Math.abs(state[i].ls) > 0.02 ||
            Math.abs(state[i].gap) > 0.02
          ) {
            state[i].nx = lerp(state[i].nx, 0, releaseSmooth);
            state[i].ny = lerp(state[i].ny, 0, releaseSmooth);
            state[i].s = lerp(state[i].s, 1, releaseSmooth);
            state[i].ls = lerp(state[i].ls, 0, releaseSmooth);
            state[i].gap = Math.max(0, lerp(state[i].gap, 0, releaseSmooth));
            letter.style.transform =
              'translate3d(' +
              state[i].nx.toFixed(2) +
              'px,' +
              state[i].ny.toFixed(2) +
              'px,0) scale(' +
              state[i].s.toFixed(4) +
              ')';
            letter.style.letterSpacing = state[i].ls.toFixed(3) + 'px';
            letter.style.marginInlineEnd =
              Math.max(0, state[i].gap).toFixed(3) + 'px';
            if (
              Math.abs(state[i].nx) < 0.04 &&
              Math.abs(state[i].ny) < 0.04 &&
              Math.abs(state[i].s - 1) < 0.003 &&
              Math.abs(state[i].ls) < 0.03 &&
              Math.abs(state[i].gap) < 0.03
            ) {
              state[i].nx = 0;
              state[i].ny = 0;
              state[i].s = 1;
              state[i].ls = 0;
              state[i].gap = 0;
              state[i].active = false;
              letter.style.transform = '';
              letter.style.letterSpacing = '';
              letter.style.marginInlineEnd = '';
            }
          }
          continue;
        }
        var dx = dxCache[i];
        var dy = dyCache[i];
        var dist = distCache[i];
        var curve = curveCache[i];
        if (curve <= 0) {
          if (
            state[i].active ||
            Math.abs(state[i].nx) > 0.02 ||
            Math.abs(state[i].ny) > 0.02 ||
            Math.abs(state[i].s - 1) > 0.002 ||
            Math.abs(state[i].ls) > 0.02 ||
            Math.abs(state[i].gap) > 0.02
          ) {
            state[i].nx = lerp(state[i].nx, 0, releaseSmooth);
            state[i].ny = lerp(state[i].ny, 0, releaseSmooth);
            state[i].s = lerp(state[i].s, 1, releaseSmooth);
            state[i].ls = lerp(state[i].ls, 0, releaseSmooth);
            state[i].gap = Math.max(0, lerp(state[i].gap, 0, releaseSmooth));
            letter.style.transform =
              'translate3d(' +
              state[i].nx.toFixed(2) +
              'px,' +
              state[i].ny.toFixed(2) +
              'px,0) scale(' +
              state[i].s.toFixed(4) +
              ')';
            letter.style.letterSpacing = state[i].ls.toFixed(3) + 'px';
            letter.style.marginInlineEnd =
              Math.max(0, state[i].gap).toFixed(3) + 'px';
            if (
              Math.abs(state[i].nx) < 0.04 &&
              Math.abs(state[i].ny) < 0.04 &&
              Math.abs(state[i].s - 1) < 0.003 &&
              Math.abs(state[i].ls) < 0.03 &&
              Math.abs(state[i].gap) < 0.03
            ) {
              state[i].nx = 0;
              state[i].ny = 0;
              state[i].s = 1;
              state[i].ls = 0;
              state[i].gap = 0;
              state[i].active = false;
              letter.style.transform = '';
              letter.style.letterSpacing = '';
              letter.style.marginInlineEnd = '';
            }
          }
          continue;
        }
        var targetScale = 1 + strength * 1.22 * curve;
        var pull = drift * curve;
        var targetNx = (dx / dist) * pull;
        var targetNy = (dy / dist) * pull;
        var glyphGapBoost = Math.min(34, rect.width * 0.28);
        var spacingMult = 2.2;
        var gapMult = 2.4;
        var targetLs = spacingMaxPx * spacingMult * curve;
        var targetGap = Math.max(
          0,
          (spacingMaxPx * gapMult + glyphGapBoost) * curve
        );

        state[i].nx = lerp(state[i].nx, targetNx, effSmooth);
        state[i].ny = lerp(state[i].ny, targetNy, effSmooth);
        state[i].s = lerp(state[i].s, targetScale, effSmooth);
        state[i].ls = lerp(state[i].ls, targetLs, effSmooth);
        state[i].gap = Math.max(0, lerp(state[i].gap, targetGap, effSmooth));
        state[i].active = true;

        letter.style.transform =
          'translate3d(' +
          state[i].nx.toFixed(2) +
          'px,' +
          state[i].ny.toFixed(2) +
          'px,0) scale(' +
          state[i].s.toFixed(4) +
          ')';
        if (spacingMaxPx > 0) {
          letter.style.letterSpacing = state[i].ls.toFixed(3) + 'px';
          letter.style.marginInlineEnd =
            Math.max(0, state[i].gap).toFixed(3) + 'px';
        } else if (state[i].ls !== 0) {
          state[i].ls = 0;
          state[i].gap = 0;
          letter.style.letterSpacing = '';
          letter.style.marginInlineEnd = '';
        }
      }
    }

    function updateLetters() {
      if (desktopSpacingEnabled) {
        updateLettersDesktop();
      } else {
        updateLettersMobileSimple();
      }
    }

    function updateSvgBend() {
      if (!svgMode || svgState.length === 0) {
        return;
      }
      var boost = params.fisheyeBoost;
      var radius = params.bubbleRadius * (svgFullMode ? 2.5 : 1.45);
      var maxAmp = (20 + params.bubbleStrength * 215) * boost;
      var spacingMaxPx = params.desktopSpacingMaxPx;
      var cap = params.desktopSpacingMaxChars;
      var svgVisc = params.viscosity;
      var release = 0.2 - svgVisc * 0.16;
      var ease = 0.32 - svgVisc * 0.22;
      var dirEase = 0.22 - svgVisc * 0.16;
      var scores = new Array(svgState.length);
      var curvesByIdx = new Array(svgState.length);
      var localXByIdx = new Array(svgState.length);
      var awaySignByIdx = new Array(svgState.length);
      var s;
      for (s = 0; s < svgState.length; s++) {
        var st0 = svgState[s];
        var rect = st0.segment.getBoundingClientRect();
        var cx = rect.left + rect.width * 0.5;
        var cy = rect.top + rect.height * 0.5;
        var dist = Math.max(0.5, Math.hypot(mx - cx, my - cy));
        var t = Math.max(0, 1 - dist / radius);
        var curve = Math.pow(t, Math.max(0.8, 1.5 - boost * 0.35));
        var localX = (mx - rect.left) / Math.max(1, rect.width);
        localXByIdx[s] = Math.max(0.08, Math.min(0.92, localX));
        awaySignByIdx[s] = cy - my >= 0 ? 1 : -1;
        scores[s] = { idx: s, curve: curve };
        curvesByIdx[s] = curve;
      }
      var mask = null;
      if (!svgFullMode && cap > 0 && cap < scores.length) {
        scores.sort(function (a, b) {
          return b.curve - a.curve;
        });
        mask = new Uint8Array(svgState.length);
        for (s = 0; s < cap; s++) {
          if (scores[s].curve > 0) {
            mask[scores[s].idx] = 1;
          }
        }
      }
      for (s = 0; s < svgState.length; s++) {
        var st = svgState[s];
        var sc = curvesByIdx[s] || 0;
        if (mask && !mask[s]) {
          sc *= 0.12;
        }
        st.rowWeight = lerp(st.rowWeight, sc, sc > st.rowWeight ? ease : release);
        var rowW = st.rowWeight;
        st.dir = lerp(st.dir, awaySignByIdx[s], dirEase);
        var awaySign = st.dir;
        st.u = lerp(st.u, localXByIdx[s], 0.22 - svgVisc * 0.12);
        var localX = st.u;
        var sigma = 0.11 + (1 - rowW) * 0.06;
        var letterLerp = 0.3 - svgVisc * 0.18;
        var g;
        for (g = 0; g < st.glyphCount; g++) {
          var baseX = st.glyphX[g];
          var u = st.width > 0 ? baseX / st.width : 0.5;
          var du = u - localX;
          var influence = Math.exp(-(du * du) / (2 * sigma * sigma));
          var centerWeight = Math.pow(influence, 0.6);
          var yBend = awaySign * maxAmp * rowW * centerWeight;
          var xPush = du * spacingMaxPx * 4.2 * rowW * centerWeight;
          var xTarget = baseX + xPush;
          var yTarget = st.baseline + yBend;
          st.glyphXNow[g] = lerp(st.glyphXNow[g], xTarget, letterLerp);
          st.glyphYNow[g] = lerp(st.glyphYNow[g], yTarget, letterLerp);
          st.glyphNodes[g].setAttribute('x', st.glyphXNow[g].toFixed(2));
          st.glyphNodes[g].setAttribute('y', st.glyphYNow[g].toFixed(2));
        }
      }
    }

    function updateSheet() {
      if (!sheet || params.sheetStrength <= 0.002) {
        return;
      }
      if (disableSheet3d) {
        return;
      }
      var rootRect = root.getBoundingClientRect();
      if (rootRect.width < 2 || rootRect.height < 2) {
      return;
      }
      var nx = (mx - rootRect.left) / rootRect.width - 0.5;
      var ny = (my - rootRect.top) / rootRect.height - 0.5;
      var tiltMul = 22;
      var st;
      var sheetLerp;
      if (desktopSpacingEnabled) {
        st = Math.min(1.2, params.sheetStrength * params.fisheyeBoost);
        var sheetLerpBase = orbital ? 0.18 : 0.1;
        sheetLerp = Math.max(
          0.04,
          sheetLerpBase * (1 - params.viscosity * 0.55)
        );
      } else {
        var MOBILE_SHEET_MULT = 1.28;
        st = Math.min(1, params.sheetStrength * MOBILE_SHEET_MULT);
        sheetLerp = orbital ? 0.18 : 0.1;
      }
      var targetRx = -ny * st * tiltMul;
      var targetRy = nx * st * tiltMul;
      var depth = 1 - Math.min(1, Math.hypot(nx * 2, ny * 2));
      var targetSc = 1 + depth * 0.065 * st;
      sheetState.rx = finiteOr(lerp(sheetState.rx, targetRx, sheetLerp), 0);
      sheetState.ry = finiteOr(lerp(sheetState.ry, targetRy, sheetLerp), 0);
      sheetState.sc = finiteOr(lerp(sheetState.sc, targetSc, sheetLerp), 1);

      sheet.style.transform =
        'perspective(' +
        params.sheetPerspective +
        'px) rotateX(' +
        sheetState.rx.toFixed(3) +
        'deg) rotateY(' +
        sheetState.ry.toFixed(3) +
        'deg) scale(' +
        sheetState.sc.toFixed(5) +
        ')';
    }

    function shouldRunLetterBubble() {
      if (orbital) {
        return inView;
      }
      return pointerInside && pointerInViewport;
    }

    function shouldRunSheetBulge() {
      if (orbital) {
        return inView;
      }
      return pointerInside && pointerInViewport;
    }

    function tick() {
      rafId = 0;

      if (orbital && inView) {
        updateOrbital(performance.now());
      }

      var skipOrbitalFx = false;
      if (orbital) {
        var nowFx = performance.now();
        if (nowFx - lastOrbitalFxMs < ORBITAL_FX_INTERVAL_MS) {
          skipOrbitalFx = true;
        } else {
          lastOrbitalFxMs = nowFx;
        }
      }

      if (!skipOrbitalFx) {
        if (shouldRunLetterBubble() && n > 0) {
          if (svgMode) {
            updateSvgBend();
          } else {
            updateLetters();
          }
        }
        if (shouldRunSheetBulge()) {
          updateSheet();
        }
      }

      if (orbital && inView) {
        rafId = window.requestAnimationFrame(tick);
      } else if (!orbital && pointerInside && pointerInViewport) {
        rafId = window.requestAnimationFrame(tick);
      }
    }

    function startLoop() {
      if (!rafId) {
        rafId = window.requestAnimationFrame(tick);
      }
    }

    function stopLoop() {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    /**
     * Safari: never call flushFxReset from these handlers (compositor glitch). Only stop/start RAF.
     * Desktop had no doc-level guards → loop kept running after the pointer left (devtools strip).
     * Orbital ignores pointer position → same. mouseout/pointerout + html mouseenter avoids mouseleave.
     */
    var safariSoftDocGuardsAttached = false;

    function safariFreezeHeavyLayers() {
      if (!isSafariWithoutChrome()) {
        return;
      }
      root.classList.add('kyoho-grape--safari-compositor-frozen');
      stopLoop();
      if (!orbital) {
        pointerInViewport = false;
      }
    }

    function safariTryResumeMotion() {
      if (!isSafariWithoutChrome()) {
        return;
      }
      if (document.hidden) {
        return;
      }
      root.classList.remove('kyoho-grape--safari-compositor-frozen');
      pointerInViewport = true;
      if (orbital) {
        if (!inView) {
          return;
        }
        bounce.lastT = performance.now();
        startLoop();
        return;
      }
      if (pointerInside) {
        startLoop();
      }
    }

    function onSafariSoftDocOut(e) {
      if (!docPointerExitToChrome(e.relatedTarget)) {
        return;
      }
      safariFreezeHeavyLayers();
    }

    function onSafariSoftHtmlEnter() {
      safariTryResumeMotion();
    }

    function onSafariWindowBlur() {
      safariFreezeHeavyLayers();
    }

    function onSafariWindowFocus() {
      safariTryResumeMotion();
    }

    function onSafariVisibilityForMotion() {
      if (document.hidden) {
        safariFreezeHeavyLayers();
      } else {
        safariTryResumeMotion();
      }
    }

    function attachSafariSoftDocGuards() {
      if (safariSoftDocGuardsAttached || !isSafariWithoutChrome()) {
        return;
      }
      safariSoftDocGuardsAttached = true;
      document.addEventListener('mouseout', onSafariSoftDocOut, true);
      document.addEventListener('pointerout', onSafariSoftDocOut, true);
      document.documentElement.addEventListener(
        'mouseenter',
        onSafariSoftHtmlEnter,
        { passive: true }
      );
      window.addEventListener('blur', onSafariWindowBlur);
      window.addEventListener('focus', onSafariWindowFocus);
      document.addEventListener(
        'visibilitychange',
        onSafariVisibilityForMotion,
        { passive: true }
      );
    }

    function detachSafariSoftDocGuards() {
      if (!safariSoftDocGuardsAttached) {
        return;
      }
      safariSoftDocGuardsAttached = false;
      document.removeEventListener('mouseout', onSafariSoftDocOut, true);
      document.removeEventListener('pointerout', onSafariSoftDocOut, true);
      document.documentElement.removeEventListener(
        'mouseenter',
        onSafariSoftHtmlEnter
      );
      window.removeEventListener('blur', onSafariWindowBlur);
      window.removeEventListener('focus', onSafariWindowFocus);
      document.removeEventListener('visibilitychange', onSafariVisibilityForMotion);
      root.classList.remove('kyoho-grape--safari-compositor-frozen');
    }

    /** Desktop: detach listeners; set in non-orbital branch. */

    if (orbital) {
      resetLetters();
      resetSvgState();
      resetSheet();

      var orbitalIo = null;

      function attachOrbitalWindowPointer() {
        window.addEventListener('pointermove', onOrbitalPointerMove, {
          passive: false,
        });
        window.addEventListener('pointerup', onOrbitalPointerUp, true);
        window.addEventListener('pointercancel', onOrbitalPointerUp, true);
      }

      function removeOrbitalWindowPointer() {
        window.removeEventListener('pointermove', onOrbitalPointerMove, {
          passive: false,
        });
        window.removeEventListener('pointerup', onOrbitalPointerUp, true);
        window.removeEventListener('pointercancel', onOrbitalPointerUp, true);
      }

      function onOrbitalPointerDown(e) {
        if (!inView) {
          return;
        }
        orbitalPointerActive = true;
        orbitalDragActive = false;
        orbitalPointerId = e.pointerId;
        orbitalStartX = e.clientX;
        orbitalStartY = e.clientY;
        orbitalLastX = e.clientX;
        orbitalLastY = e.clientY;
        orbitalLastT = performance.now();
        orbitalDragVx = 0;
        orbitalDragVy = 0;
        savedOrbitalVx = bounce.vx;
        savedOrbitalVy = bounce.vy;
        bounce.vx = 0;
        bounce.vy = 0;
        attachOrbitalWindowPointer();
      }

      function onOrbitalPointerMove(e) {
        if (!orbitalPointerActive || e.pointerId !== orbitalPointerId) {
          return;
        }
        var dx = e.clientX - orbitalStartX;
        var dy = e.clientY - orbitalStartY;
        if (!orbitalDragActive && Math.hypot(dx, dy) >= ORBITAL_DRAG_THRESHOLD) {
          orbitalDragActive = true;
        }
        if (orbitalDragActive) {
          var t = performance.now();
          var dt = (t - orbitalLastT) / 1000;
          if (dt > 0.0008 && dt < 0.12) {
            orbitalDragVx = (e.clientX - orbitalLastX) / dt;
            orbitalDragVy = (e.clientY - orbitalLastY) / dt;
          }
          orbitalLastX = e.clientX;
          orbitalLastY = e.clientY;
          orbitalLastT = t;
          pendingDragClientX = e.clientX;
          pendingDragClientY = e.clientY;
          if (!orbitalDragRafId) {
            orbitalDragRafId = window.requestAnimationFrame(flushOrbitalDragPaint);
          }
          e.preventDefault();
        }
      }

      function onOrbitalPointerUp(e) {
        if (!orbitalPointerActive || e.pointerId !== orbitalPointerId) {
          return;
        }
        var wasDrag = orbitalDragActive;
        if (orbitalDragRafId) {
          window.cancelAnimationFrame(orbitalDragRafId);
          orbitalDragRafId = 0;
        }
        if (wasDrag) {
          var wwUp = window.innerWidth;
          var whUp = window.innerHeight;
          var mUp = Math.max(40, Math.min(wwUp, whUp) * 0.08);
          bounce.x = Math.max(mUp, Math.min(wwUp - mUp, orbitalLastX));
          bounce.y = Math.max(mUp, Math.min(whUp - mUp, orbitalLastY));
          syncOrbitalCursor();
        }
        orbitalPointerActive = false;
        orbitalDragActive = false;
        orbitalPointerId = null;
        removeOrbitalWindowPointer();

        if (wasDrag) {
          e.preventDefault();
          var scale = orbitalSpeedScale();
          var minSpeed = 100 * scale;
          var maxSpeed = 420 * scale;
          var sp = Math.hypot(orbitalDragVx, orbitalDragVy);
          if (sp < 8) {
            bounce.vx = savedOrbitalVx;
            bounce.vy = savedOrbitalVy;
            if (Math.hypot(bounce.vx, bounce.vy) < 20) {
              var ang = Math.random() * Math.PI * 2;
              bounce.vx = Math.cos(ang) * minSpeed;
              bounce.vy = Math.sin(ang) * minSpeed;
            }
          } else {
            var nx = orbitalDragVx / sp;
            var ny = orbitalDragVy / sp;
            var targetSp = Math.min(maxSpeed, Math.max(minSpeed, sp));
            bounce.vx = nx * targetSp;
            bounce.vy = ny * targetSp;
          }
          bounce.lastT = performance.now();
        } else {
          bounce.vx = savedOrbitalVx;
          bounce.vy = savedOrbitalVy;
          bounce.lastT = performance.now();
        }
      }

      function onOrbitalResize() {
        if (!bounce.ready) {
          return;
        }
        var w = window.innerWidth;
        var h = window.innerHeight;
        var margin = Math.max(40, Math.min(w, h) * 0.08);
        clampBounceToViewport(w, h, margin);
        mx = bounce.x;
        my = bounce.y;
      }

      function detachOrbitalCore() {
        detachSafariSoftDocGuards();
        if (orbitalDragRafId) {
          window.cancelAnimationFrame(orbitalDragRafId);
          orbitalDragRafId = 0;
        }
        if (orbitalPointerActive) {
          removeOrbitalWindowPointer();
          orbitalPointerActive = false;
          orbitalDragActive = false;
          orbitalPointerId = null;
        }
        if (orbitalIo) {
          orbitalIo.disconnect();
          orbitalIo = null;
        }
        window.removeEventListener('resize', onOrbitalResize);
        root.removeEventListener('pointerdown', onOrbitalPointerDown, true);
      }

      root.addEventListener('pointerdown', onOrbitalPointerDown, true);

      window.addEventListener('resize', onOrbitalResize, { passive: true });

      orbitalIo = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          inView = !!(entry && entry.isIntersecting);
          if (inView) {
            if (isSafariWithoutChrome()) {
              root.classList.remove('kyoho-grape--safari-compositor-frozen');
            }
            bounce.lastT = performance.now();
            startLoop();
          } else {
            if (isSafariWithoutChrome()) {
              root.classList.add('kyoho-grape--safari-compositor-frozen');
            }
            stopLoop();
            if (orbitalDragRafId) {
              window.cancelAnimationFrame(orbitalDragRafId);
              orbitalDragRafId = 0;
            }
            if (orbitalPointerActive) {
              removeOrbitalWindowPointer();
              orbitalPointerActive = false;
              orbitalDragActive = false;
              orbitalPointerId = null;
            }
            var runOrbitalIoReset = function () {
              resetLetters();
              resetSvgState();
              resetSheet();
              bounce.ready = false;
            };
            if (isSafariWithoutChrome()) {
              window.requestAnimationFrame(function () {
                window.requestAnimationFrame(runOrbitalIoReset);
              });
            } else {
              runOrbitalIoReset();
            }
          }
        },
        { threshold: 0.08, rootMargin: '40px' }
      );
      orbitalIo.observe(root);
      startLoop();
      attachSafariSoftDocGuards();

      onSectionUnloadCleanup = detachOrbitalCore;
    } else {
      function flushFxReset() {
        resetLetters();
        resetSvgState();
        resetSheet();
        if (cursor) {
          cursor.classList.remove('is-visible');
        }
      }

      function markViewportLeft() {
        pointerInViewport = false;
        stopLoop();
        flushFxReset();
      }

      function markViewportEntered() {
        pointerInViewport = true;
        if (pointerInside) {
          startLoop();
        }
      }

      function onDocMouseOut(e) {
        if (e.relatedTarget === null) {
          markViewportLeft();
        }
      }

      function onWinMove(e) {
        mx = e.clientX;
        my = e.clientY;
        if (cursor) {
          cursor.classList.add('is-visible');
          cursor.style.setProperty('--cx', mx + 'px');
          cursor.style.setProperty('--cy', my + 'px');
        }
        if (pointerInside && pointerInViewport) {
          startLoop();
        }
      }

      function onSecEnter() {
        pointerInside = true;
        if (cursor) {
          cursor.classList.add('is-visible');
        }
        if (pointerInViewport) {
          startLoop();
        }
      }

      function onSecLeave() {
        pointerInside = false;
        stopLoop();
        /*
          Safari: flushFxReset on pointer-exit tears down many transforms while the compositor is
          transitioning into docked devtools / chrome — often the solid-red GPU glitch. Only stop
          the loop and hide the cursor; letters/sheet state is left for the next hover tick.
        */
        if (isSafariWithoutChrome()) {
          if (cursor) {
            cursor.classList.remove('is-visible');
          }
          return;
        }
        window.requestAnimationFrame(function () {
          flushFxReset();
        });
      }

      var desktopListenersAttached = false;
      /** documentElement / capture mouseout — omitted on Safari (see onSecLeave). */
      var desktopDocPointerGuardsAttached = false;

      function attachDesktop() {
        if (desktopListenersAttached) {
          return;
        }
        desktopListenersAttached = true;
        if (!isSafariWithoutChrome()) {
          desktopDocPointerGuardsAttached = true;
          document.documentElement.addEventListener('mouseleave', markViewportLeft);
          document.documentElement.addEventListener('mouseenter', markViewportEntered);
          document.addEventListener('mouseout', onDocMouseOut, true);
        } else {
          attachSafariSoftDocGuards();
        }
        window.addEventListener('mousemove', onWinMove, { passive: true });
        root.addEventListener('mouseenter', onSecEnter, { passive: true });
        root.addEventListener('mouseleave', onSecLeave, { passive: true });
        if (root.matches(':hover')) {
          onSecEnter();
        }
      }

      function detachDesktop() {
        if (!desktopListenersAttached) {
          return;
        }
        desktopListenersAttached = false;
        detachSafariSoftDocGuards();
        if (desktopDocPointerGuardsAttached) {
          desktopDocPointerGuardsAttached = false;
          document.documentElement.removeEventListener('mouseleave', markViewportLeft);
          document.documentElement.removeEventListener('mouseenter', markViewportEntered);
          document.removeEventListener('mouseout', onDocMouseOut, true);
        }
        window.removeEventListener('mousemove', onWinMove);
        root.removeEventListener('mouseenter', onSecEnter);
        root.removeEventListener('mouseleave', onSecLeave);
      }

      var ioDesk = new IntersectionObserver(
        function (entries) {
          if (entries[0] && entries[0].isIntersecting) {
            ioDesk.disconnect();
            window.requestAnimationFrame(function () {
              window.requestAnimationFrame(attachDesktop);
            });
          }
        },
        { rootMargin: '140px 0px 280px 0px', threshold: 0 }
      );
      ioDesk.observe(root);

      window.requestAnimationFrame(function () {
        var cr = root.getBoundingClientRect();
        if (cr.top < window.innerHeight + 160 && cr.bottom > -160) {
          ioDesk.disconnect();
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(attachDesktop);
          });
        }
      });

      onSectionUnloadCleanup = detachDesktop;
    }

  }

  function boot() {
    document.querySelectorAll('[data-kyoho-grape-section]').forEach(initSection);
  }

  function bootDeferred() {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(boot);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootDeferred);
  } else {
    bootDeferred();
  }
})();
